
const COLUMNS = 8;

const DESKTOP_SPANS = {
  landscape: [2, 1],
  square: [2, 2],
  portrait: [1, 2],
  hero: [4, 2],
  filler: [1, 1],
};

const MOBILE_SPANS = {
  landscape: [4, 2],
  square: [4, 4],
  portrait: [4, 8],
  hero: [8, 4],
  filler: [4, 4],
};

// Queue order, which also breaks ties in the interleave.
const SHAPES = ['landscape', 'square', 'portrait', 'hero', 'filler'];

const SIZES = {
  landscape: '(min-width: 1200px) 320px, (min-width: 768px) 25vw, 50vw',
  square: '(min-width: 1200px) 460px, (min-width: 768px) 36vw, 70vw',
  portrait: '(min-width: 1200px) 200px, (min-width: 768px) 16vw, 66vw',
  filler: '(min-width: 1200px) 230px, (min-width: 768px) 18vw, 72vw',
  hero: '(min-width: 1200px) 640px, (min-width: 768px) 50vw, 100vw',
};

// One landscape in every HERO_INTERVAL is promoted to a hero
const HERO_INTERVAL = 12;

const DESKTOP_QUERY = '(min-width: 768px)';

const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;

let measuredWidth = -1;
let measuredCell = 0;

const measureCell = (grid) => {
  const width = grid.clientWidth;
  if (width === measuredWidth) {
    return measuredCell;
  }

  const tracks = window.getComputedStyle(grid).gridTemplateColumns.split(' ');
  const cell = parseFloat(tracks[0]);
  if (!cell) {
    return 0;
  }

  measuredWidth = width;
  measuredCell = cell;
  grid.style.setProperty('--gallery-cell', `${cell}px`);
  return cell;
};

// The shape the server derived from the image ratio. Cached on first sight because
// hero promotion overwrites data-shape, and the intrinsic value must survive that.
const baseShape = (item) => {
  if (!item.dataset.baseShape) {
    const shape = item.getAttribute('data-shape');
    item.dataset.baseShape = DESKTOP_SPANS[shape] ? shape : 'landscape';
  }
  return item.dataset.baseShape;
};

const buildEntries = (items) => {
  let landscapes = 0;
  return items.map((item) => {
    let shape = baseShape(item);
    if (shape === 'landscape') {
      landscapes += 1;
      if (landscapes % HERO_INTERVAL === 0) {
        shape = 'hero';
      }
    }
    return { item, shape };
  });
};

const bucket = (entries) => {
  const queues = {};
  SHAPES.forEach((shape) => {
    queues[shape] = [];
  });
  entries.forEach((entry) => {
    queues[entry.shape].push(entry);
  });
  return queues;
};

const interleave = (queues) => {
  const keys = SHAPES.filter(shape => queues[shape].length > 0);
  const total = keys.reduce((sum, key) => sum + queues[key].length, 0);
  const taken = {};
  keys.forEach((key) => {
    taken[key] = 0;
  });

  const ordered = [];
  while (ordered.length < total) {
    let pick = null;
    let bestDeficit = -Infinity;
    keys.forEach((key) => {
      if (taken[key] >= queues[key].length) {
        return;
      }
      const share = queues[key].length / total;
      const deficit = (share * (ordered.length + 1)) - taken[key];
      if (deficit > bestDeficit) {
        bestDeficit = deficit;
        pick = key;
      }
    });
    if (pick === null) {
      break;
    }
    ordered.push(queues[pick][taken[pick]]);
    taken[pick] += 1;
  }
  return ordered;
};

const rowAt = (occupancy, index) => {
  while (occupancy.length <= index) {
    const row = [];
    for (let col = 0; col < COLUMNS; col += 1) {
      row.push(false);
    }
    occupancy.push(row);
  }
  return occupancy[index];
};

const fits = (occupancy, row, col, cols, rows) => {
  if (col + cols > COLUMNS) {
    return false;
  }
  for (let r = row; r < row + rows; r += 1) {
    const target = rowAt(occupancy, r);
    for (let c = col; c < col + cols; c += 1) {
      if (target[c]) {
        return false;
      }
    }
  }
  return true;
};

const occupy = (occupancy, row, col, cols, rows) => {
  for (let r = row; r < row + rows; r += 1) {
    const target = rowAt(occupancy, r);
    for (let c = col; c < col + cols; c += 1) {
      target[c] = true;
    }
  }
};

const findSlot = (occupancy, cols, rows) => {
  const limit = occupancy.length + rows + 1;
  for (let row = 0; row < limit; row += 1) {
    for (let col = 0; col + cols <= COLUMNS; col += 1) {
      if (fits(occupancy, row, col, cols, rows)) {
        return { row, col };
      }
    }
  }
  return { row: occupancy.length, col: 0 };
};

const firstEmptyCell = (occupancy) => {
  for (let row = 0; row < occupancy.length; row += 1) {
    for (let col = 0; col < COLUMNS; col += 1) {
      if (!occupancy[row][col]) {
        return { row, col };
      }
    }
  }
  return null;
};

// Walks the interleaved list keeping the grid gapless. At each step the earliest
// hole is found and the first upcoming tile that fits it is pulled forward; if none
// fits, a square is demoted to a 1x1 filler, which fits almost anything.
const pack = (ordered, spans) => {
  const occupancy = [];
  const remaining = ordered.slice();
  const placed = [];

  while (remaining.length > 0) {
    const gap = firstEmptyCell(occupancy);
    let index = -1;

    if (gap) {
      index = remaining.findIndex((entry) => {
        const [cols, rows] = spans[entry.shape];
        return fits(occupancy, gap.row, gap.col, cols, rows);
      });

      if (index === -1) {
        const candidate = remaining.findIndex(entry => entry.shape === 'square');
        if (candidate !== -1) {
          const [cols, rows] = spans.filler;
          if (fits(occupancy, gap.row, gap.col, cols, rows)) {
            remaining[candidate].shape = 'filler';
            index = candidate;
          }
        }
      }
    }

    const entry = remaining.splice(index === -1 ? 0 : index, 1)[0];
    const [cols, rows] = spans[entry.shape];
    const slot = findSlot(occupancy, cols, rows);
    occupy(occupancy, slot.row, slot.col, cols, rows);
    placed.push(entry);
  }

  return placed;
};

const upgradeHeroImage = (item) => {
  const image = item.querySelector('img');
  const full = item.getAttribute('data-src');
  if (!image || !full) {
    return;
  }
  if (image.classList.contains('lazy')) {
    image.dataset.src = full;
    return;
  }
  if (image.srcset !== full) {
    image.srcset = full;
  }
};


const applyImageSizing = (item, shape) => {
  const image = item.querySelector('img');
  if (!image) {
    return;
  }

  const sizes = SIZES[shape];
  if (sizes && image.getAttribute('sizes') !== sizes) {
    image.setAttribute('sizes', sizes);
  }

  if (shape === 'hero') {
    upgradeHeroImage(item);
  } else if (image.hasAttribute('data-src')) {
    image.removeAttribute('data-src');
  }
};

const applyOrder = (grid, placed) => {
  const fragment = document.createDocumentFragment();
  placed.forEach((entry) => {
    if (entry.item.getAttribute('data-shape') !== entry.shape) {
      entry.item.setAttribute('data-shape', entry.shape);
    }
    applyImageSizing(entry.item, entry.shape);
    fragment.appendChild(entry.item);
  });
  grid.appendChild(fragment);
};

const logDistribution = (entries) => {
  if (window.location.search.indexOf('tiledebug') === -1) {
    return;
  }
  const counts = {};
  entries.forEach((entry) => {
    counts[entry.shape] = (counts[entry.shape] || 0) + 1;
  });
  // eslint-disable-next-line no-console
  console.info('[gallery tiles]', entries.length, 'visible', counts);
};

const applyTilePattern = (grid, visibleItems) => {
  if (!grid) {
    return;
  }
  measureCell(grid);
  if (!visibleItems || visibleItems.length === 0) {
    return;
  }
  const spans = isDesktop() ? DESKTOP_SPANS : MOBILE_SPANS;
  const entries = buildEntries(visibleItems);
  logDistribution(entries);
  applyOrder(grid, pack(interleave(bucket(entries)), spans));
};

export { isDesktop, measureCell };
export default applyTilePattern;
